/**
 * Legal page content for the public footer documents.
 *
 * The text below is imported from the provided final TXT documents. It is treated as
 * document content, not as executable instructions.
 */
import type { LegalDocument, LegalDocumentId } from '../types/domain';

type LegalSource = {
  id: LegalDocumentId;
  title: string;
  summary: string;
  version: string;
  text: string;
};

const TERMS_TEXT = `TERMS OF SERVICE

Last Updated: September 5, 2026

These Terms of Service (“Terms”) govern your access to and use of Lyrixsa AI, including the Lyrixsa AI website, application, software, tools, features, content, services, and related products (collectively, the “Services”).

Lyrixsa AI is a product of Geecore Limited (“Geecore,” “we,” “us,” or “our”).

By accessing or using Lyrixsa AI, you agree to be legally bound by these Terms. If you do not agree with these Terms, you must not access or use the Services.


1. TERMS

By accessing or using Lyrixsa AI, you agree to comply with these Terms, all applicable laws and regulations, and any additional rules, policies, or guidelines that we may publish from time to time.

You are responsible for ensuring that your use of Lyrixsa AI complies with the laws applicable to you.

Lyrixsa AI provides artificial-intelligence-powered tools that may allow users to create, transform, analyze, edit, organize, license, prepare, and publish music and related content.

Certain features may depend upon third-party services, technologies, APIs, music platforms, distribution services, payment processors, or other external providers.

We may modify, suspend, discontinue, or replace any portion of the Services at any time.


2. USE LICENSE

2.1 Website and Software License

Permission is granted to access and use the Lyrixsa AI website and Services for their intended purposes, subject to these Terms.

This is a license to use the Services, not a transfer of ownership.

Except where expressly permitted by these Terms or your applicable Lyrixsa license, you may not:

1. modify, copy, reproduce, or redistribute the Lyrixsa AI software, website, source code, interface, or proprietary materials;
2. reproduce or commercially exploit Lyrixsa AI’s proprietary materials or technology outside the Services;
3. attempt to decompile, reverse engineer, disassemble, or otherwise derive the source code of the Services;
4. remove copyright, trademark, ownership, or proprietary notices;
5. rent, lease, sublicense, sell, resell, or transfer access to your Lyrixsa AI account;
6. create a competing service using Lyrixsa AI’s proprietary software, interface, or confidential information;
7. use automated systems to scrape, copy, harvest, or extract data from the Services except where expressly authorized;
8. interfere with or attempt to compromise the security, integrity, or operation of the Services;
9. circumvent usage limits, credit limits, access controls, or other technical restrictions;
10. use the Services to violate applicable laws or the rights of another person.

2.2 Account Access

Your Lyrixsa AI account is personal to you unless your applicable subscription or business agreement expressly permits multiple users.

You are responsible for maintaining the confidentiality of your login credentials and for activities conducted through your account.

You must notify us promptly if you believe your account has been accessed without authorization.


3. AI-GENERATED MUSIC AND CONTENT

Lyrixsa AI uses artificial-intelligence technologies to generate and process music and related content.

AI-generated results may vary from one generation to another and may not always produce the result you expect.

You acknowledge that:

1. AI-generated content may contain similarities to existing musical works or other content;
2. Lyrixsa AI does not guarantee that every generated result will be unique in every respect;
3. Lyrixsa AI does not guarantee that generated content will qualify for copyright protection in every country;
4. copyright and other intellectual-property laws differ between jurisdictions;
5. you are responsible for determining whether and how you may legally use generated content in your particular circumstances;
6. you should not represent AI-generated content as being wholly human-created where doing so would be misleading or unlawful.

Lyrixsa AI may apply technical safeguards, content filters, watermarking, moderation, or other mechanisms to AI-generated content.


4. YOUR CONTENT

You may provide prompts, lyrics, images, audio, song references, artwork, text, and other materials to Lyrixsa AI (“User Content”).

You retain ownership of your User Content, subject to the rights and licenses necessary for us to operate the Services.

You represent and warrant that you have all necessary rights, permissions, licenses, and authorizations to submit User Content to Lyrixsa AI and to use it for the purpose for which you submit it.

You must not upload, submit, or process content that you do not have the legal right to use.

This includes, without limitation:

- copyrighted music without appropriate permission;
- lyrics belonging to third parties without authorization;
- recordings you do not own or have permission to process;
- unauthorized samples;
- private or confidential material;
- trademarks or branding that you do not have permission to use;
- personal information belonging to another person without appropriate authorization.


5. SONG REFERENCES, SPOTIFY LINKS AND THIRD-PARTY MUSIC

Lyrixsa AI may provide tools that allow you to submit supported music references or links for analysis, inspiration, or other permitted functionality.

Providing a link to a song does not transfer ownership of that song to you or to Lyrixsa AI.

You are responsible for ensuring that your use of any referenced music complies with applicable copyright, licensing, platform, and other legal requirements.

You may not use Lyrixsa AI to reproduce, distribute, exploit, or otherwise infringe third-party copyrighted music.

Lyrixsa AI may restrict or block requests involving copyrighted lyrics, unauthorized reproduction, artist impersonation, or other prohibited uses.


6. MUSIC LICENSING AND COMMERCIAL USE

Certain Lyrixsa AI plans may include commercial-use rights, licensing documentation, certificates, or other licensing-related features.

Where applicable, the rights granted to you are determined by:

1. the Lyrixsa AI plan you purchased;
2. the applicable license or certificate provided by Lyrixsa AI;
3. these Terms;
4. any additional licensing terms presented at the time of purchase.

A Lyrixsa AI subscription or purchase does not automatically grant ownership of third-party music, samples, recordings, lyrics, trademarks, or other intellectual property.

Where Lyrixsa AI provides a commercial-use license for eligible generated music, that license applies only to music and usage that satisfy the applicable license terms.

You remain responsible for ensuring that your intended use complies with the applicable license.

No license granted by Lyrixsa AI authorizes you to claim ownership of third-party intellectual property.


7. PUBLISHING AND MUSIC DISTRIBUTION

Lyrixsa AI may provide tools or services designed to help users prepare music for publishing or distribution to third-party music platforms.

These platforms may include services such as Spotify, Apple Music, YouTube Music, Amazon Music, Tidal, Deezer, TikTok, Instagram/Facebook, Boomplay, Tencent/QQ Music, and other platforms that may become available.

Availability of any particular platform is not guaranteed.

Third-party platforms maintain their own:

- terms of service;
- content policies;
- copyright policies;
- eligibility requirements;
- artist requirements;
- monetization rules;
- distribution requirements.

Lyrixsa AI does not guarantee that any third-party platform will accept, publish, distribute, monetize, or maintain your music.

A distribution submission may be rejected, delayed, removed, demonetized, claimed, or otherwise restricted by a third-party platform.

You remain responsible for providing accurate metadata and ensuring that you have the rights necessary to distribute the music you submit.


8. ROYALTIES AND EARNINGS

Where Lyrixsa AI provides features relating to music publishing, distribution, royalties, or monetization, any earnings depend on factors outside our control.

Lyrixsa AI does not guarantee:

- streams;
- listeners;
- followers;
- downloads;
- sales;
- royalties;
- publishing income;
- advertising revenue;
- viral performance;
- playlist placement;
- platform acceptance;
- or any specific financial result.

Any earnings examples, calculations, estimates, demonstrations, or references displayed by Lyrixsa AI are for illustrative purposes unless expressly stated otherwise.


9. USER RESPONSIBILITIES

You agree not to use Lyrixsa AI to:

1. infringe copyright, trademark, publicity, privacy, or other rights;
2. impersonate another artist or person without authorization;
3. generate unauthorized copies of copyrighted works;
4. generate or distribute copyrighted lyrics without permission;
5. circumvent copyright-protection systems;
6. create fraudulent or deceptive content;
7. upload malicious software or harmful code;
8. interfere with the operation of Lyrixsa AI;
9. abuse credits, API access, generation systems, or other resources;
10. attempt to gain unauthorized access to another account;
11. resell access to the Services without authorization;
12. use the Services in violation of applicable law;
13. use the Services for any prohibited or unlawful purpose.

We reserve the right to suspend or terminate accounts that violate these requirements.


10. CREDITS, GENERATIONS AND USAGE LIMITS

Certain Lyrixsa AI features may operate using credits, generation limits, storage limits, subscription limits, or other usage restrictions.

Credits and usage allowances may vary depending on your plan.

Unless otherwise stated at the time of purchase:

- unused applicable credits may remain available according to the terms of your plan;
- credits have no cash value;
- credits cannot be transferred between accounts;
- credits cannot be redeemed for cash;
- credits may not be sold or resold;
- additional credits may be purchased where offered.

We reserve the right to impose reasonable usage limits to protect the Services from abuse or excessive automated usage.


11. FAIR USE POLICY

11.1 Purpose

Lyrixsa AI is designed to provide AI-powered music creation, editing, licensing, publishing, and distribution tools for genuine creative use. Certain plans may advertise or provide “Unlimited” music creation or “Unlimited” publishing.

“Unlimited” means that eligible users may use the applicable feature without a fixed credit-based quantity limit, subject to this Fair Use Policy, the technical limits of the Services, the applicable plan terms, and the rules of our third-party service and distribution partners.

Unlimited access does not mean unlimited use for automated abuse, system exploitation, spam, resale, or activity that is unreasonable in relation to the intended use of the Services.

11.2 Fair Use for Unlimited Music Creation

Where a plan includes unlimited music creation, ordinary human use of the music-generation features is permitted without a predetermined number of songs or generations.

However, Lyrixsa AI may apply reasonable safeguards, rate limits, concurrency limits, temporary restrictions, or other usage controls where necessary to protect the Services, third-party AI providers, infrastructure, or other users.

Examples of use that may fall outside fair use include, without limitation:

- using bots, scripts, crawlers, macros, or other automated systems to generate music at scale without authorization;
- running unusually high-volume or continuous generation activity designed to consume disproportionate system resources;
- using multiple accounts or identities to circumvent usage controls or technical restrictions;
- sharing an account or subscription with unauthorized users;
- using Lyrixsa AI as an unauthorized backend or bulk-generation service for another business, application, platform, or product;
- attempting to bypass rate limits, concurrency controls, credits, access controls, or other technical safeguards;
- reselling, sublicensing, or commercially redistributing access to Lyrixsa AI without authorization; or
- engaging in activity that materially degrades, disrupts, overloads, or interferes with the Services or other users’ access.

11.3 Fair Use Does Not Create a Fixed Generation Cap

Unless a specific plan or offer expressly states otherwise, Lyrixsa AI does not intend to impose an arbitrary fixed number of music generations on an eligible Unlimited Music Creation plan.

The fact that a user creates a large number of songs does not, by itself, constitute misuse. The relevant consideration is whether the activity is consistent with the intended human use of the Services and whether it places unreasonable or abusive demands on the platform or its providers.

11.4 Fair Use for Music Publishing and Distribution

Music creation and music publishing/distribution are separate operational functions.

An Unlimited Song Creation & Expanded Publisher Monetization plan may provide expanded publishing subject to this Fair Use Policy. This does not require Lyrixsa AI or its distribution partners to accept an unlimited number of releases regardless of volume, quality, metadata, rights status, or platform requirements.

For standard publishing workflows, Lyrixsa AI may specify a normal publishing allowance or require publisher credits for additional publishing where stated in the applicable offer or plan.

Where an Unlimited plan removes a stated publishing allowance, publishing remains subject to reasonable fair-use controls and the requirements of Lyrixsa AI’s distribution partners and third-party music platforms.

For example, the Unlimited Song Creation & Expanded Publisher Monetization offer may permit expanded publishing for ordinary use, while Lyrixsa AI reserves the right to review, limit, delay, pause, or otherwise control unusually high-volume or abusive release activity.

11.5 Examples of Publishing or Distribution Activity Outside Fair Use

The following may be considered outside fair use, depending on the circumstances:

- mass submission of very large numbers of releases in a short period primarily to exploit distribution or royalty systems;
- uploading large quantities of duplicate, substantially identical, or minimally altered tracks;
- submitting music with inaccurate, misleading, incomplete, fabricated, or manipulated metadata;
- repeatedly submitting content that does not satisfy distributor or platform requirements;
- creating releases primarily for spam, artificial catalog inflation, or search manipulation;
- submitting content for which the user does not possess the required rights or permissions;
- submitting unauthorized samples, recordings, lyrics, artwork, or other third-party material;
- attempting to manipulate streaming, royalty, Content ID, fingerprinting, recommendation, or platform systems;
- making false or fraudulent ownership, copyright, rights-management, or artist claims;
- impersonating another artist or falsely suggesting endorsement, affiliation, or authorship;
- using multiple accounts to circumvent publishing, distribution, review, or account restrictions; or
- any other activity that reasonably appears intended to abuse, exploit, circumvent, or undermine Lyrixsa AI’s publishing or distribution infrastructure or the systems of its partners.

11.6 Distribution Partner and Platform Requirements

Music submitted for publishing or distribution may be reviewed against the requirements of the applicable distributor, rights-management provider, streaming service, social platform, or other third-party service.

These third parties may impose their own limits, review procedures, metadata requirements, content rules, artist requirements, copyright requirements, fraud controls, release limits, or other restrictions.

Lyrixsa AI cannot guarantee that a particular number of releases will be accepted, delivered, published, monetized, or maintained by any third-party platform.

A commercial license from Lyrixsa AI does not override the rules of a distributor, streaming platform, social network, rights-management service, or other third party.

11.7 Fair Use and Commercial Rights

Fair-use restrictions concern how the Lyrixsa AI Services may be used. They do not, by themselves, remove or reduce a commercial license that was validly granted for eligible Generated Music, except where the license or applicable terms expressly provide otherwise.

The Commercial Music License Agreement governs the commercial rights granted for eligible music.

Users must continue to comply with the Commercial Music License Agreement, these Terms, applicable laws, and the rules of relevant third-party platforms.

11.8 Review and Enforcement

If Lyrixsa AI reasonably determines that activity may exceed fair use or create a material risk to the Services, its users, or third-party partners, we may take reasonable measures, including:

- temporarily slowing or limiting generation activity;
- applying rate or concurrency limits;
- temporarily restricting publishing or distribution submissions;
- placing releases or accounts into manual review;
- requiring additional information or verification;
- delaying or rejecting particular submissions;
- requiring use of applicable publisher credits or another appropriate plan for sustained bulk usage;
- restricting specific features;
- suspending an account or feature where necessary to protect the Services; or
- terminating access for serious, repeated, fraudulent, unlawful, or abusive misuse.

Where reasonably practicable, Lyrixsa AI may provide notice and an opportunity to address the issue before taking longer-term action, except where immediate action is necessary to protect the Services, users, third parties, security, legal rights, or platform integrity.

11.9 No Guaranteed Publishing Volume

Nothing in an “Unlimited Publishing” or similar offer guarantees a particular number of releases, a particular distribution volume, or acceptance by any third-party distributor or music platform.

Operational limits may change where required by third-party providers, distribution partners, technical capacity, fraud-prevention requirements, platform rules, or other legitimate operational considerations.

11.10 Copyright, Fair Use and Third-Party Material

Lyrixsa AI may provide features that allow users to submit song references, supported music links, lyrics, audio, images, or other material for analysis, inspiration, transformation, or other permitted functionality.

Providing a reference or link does not transfer ownership of the referenced work to the user or to Lyrixsa AI.

Users must have the rights, permissions, or other lawful basis necessary to submit material to the Services and to use the resulting output for their intended purpose.

Copyright exceptions such as fair use may apply in some circumstances, but fair use is a case-by-case legal determination. Lyrixsa AI does not determine that a particular use qualifies as fair use and does not provide legal advice.

Users should not assume that a short portion, limited duration, percentage, number of words, musical notes, or other fixed amount of copyrighted material is automatically lawful.

11.11 Artist Imitation and Voice Use

Users must not use Lyrixsa AI to falsely represent that a real artist created, performed, endorsed, authorized, or sponsored content when that is not true.

Users must have appropriate rights and consent when using another person's voice, name, likeness, identity, or other protected personal attributes.

Using general genre or musical characteristics as inspiration is different from falsely impersonating a particular artist.

11.12 Distribution, Content ID and Rights Claims

Before distributing music through Spotify, Apple Music, YouTube Music, TikTok, Instagram/Facebook, Amazon Music, Tidal, Deezer, Boomplay, Tencent/QQ Music, or another platform, users must comply with the current rules of the relevant distributor and platform.

The existence of a Lyrixsa commercial license does not guarantee acceptance into Content ID, rights-management, fingerprinting, or other automated copyright systems.

Users must not submit false copyright claims or attempt to claim exclusive ownership over material they do not have the legal right to control.

11.13 Responsibility for Outputs

Users are responsible for reviewing generated music and other outputs before commercial release, publishing, distribution, licensing, monetization, or other use.

Users remain responsible for ensuring that their intended use complies with applicable law, the applicable Lyrixsa license, and third-party platform requirements.

11.14 Reporting Misuse

If you believe a user is using Lyrixsa AI to infringe copyright, violate another person's rights, impersonate an artist, manipulate distribution or royalty systems, or otherwise misuse the Services, you may contact us through:

https://app.lyrixsa.com/support

or

support@lyrixsa.com

Where available, reports should include sufficient information to identify the relevant account, track, release, URL, or alleged violation.

11.15 No Legal Determination

Lyrixsa AI cannot determine whether a particular use of copyrighted material qualifies as fair use.

Only a court or other legally authorized authority may make a binding legal determination in a particular dispute.

This Fair Use Policy describes platform rules and operational safeguards and is not legal advice.

11.16 International Users

Fair-use rules and copyright exceptions vary by jurisdiction. The U.S. concept of “fair use” may differ from copyright exceptions or “fair dealing” rules applicable in other countries.

If you use Lyrixsa AI outside the United States, you are responsible for complying with the laws applicable to your use.

11.17 Changes to Fair Use Controls

Lyrixsa AI may update, refine, or modify fair-use controls as the Services, technology, distribution relationships, third-party requirements, and operational needs change.

Any such controls will be applied in a manner consistent with the applicable plan terms and applicable law.


12. THIRD-PARTY SERVICES

Lyrixsa AI may integrate with or rely upon third-party services, including music platforms, payment processors, artificial-intelligence providers, cloud infrastructure providers, distribution services, analytics providers, and other external services.

Your use of third-party services is subject to their respective terms and privacy policies.

We are not responsible for changes, interruptions, outages, restrictions, pricing changes, or policies imposed by third-party services.


13. PAYMENTS, SUBSCRIPTIONS AND PURCHASES

You agree to provide accurate billing and payment information when purchasing Lyrixsa AI.

Prices, plans, features, bonuses, credits, and payment terms may change from time to time.

Any purchase is subject to the pricing and terms displayed at the time of purchase.

Certain purchases may be one-time payments, subscriptions, upgrades, add-ons, credits, certifications, or other products.

Where a subscription is offered, it will continue according to the subscription terms presented at checkout unless cancelled in accordance with those terms.


14. REFUND POLICY

Unless otherwise stated for a specific offer, eligible purchases may be covered by a 14-day refund policy.

To request a refund, you must contact Lyrixsa AI support within the applicable refund period through:

https://app.lyrixsa.com/support

or by email at:

support@lyrixsa.com

Refund eligibility may be subject to the specific terms displayed at the time of purchase.

Once a refund has been issued, your access to the refunded product, license, credits, or associated benefits may be revoked.


15. DISCLAIMER

The Services and all materials provided by Lyrixsa AI are provided on an “AS IS” and “AS AVAILABLE” basis.

Geecore Limited makes no warranties, express or implied, regarding the Services, including warranties of:

- merchantability;
- fitness for a particular purpose;
- non-infringement;
- accuracy;
- availability;
- reliability;
- uninterrupted operation;
- or suitability for any particular use.

We do not guarantee that:

1. the Services will always be available;
2. generated content will always meet your expectations;
3. generated music will be unique;
4. generated content will receive copyright protection;
5. content will be accepted by a third-party platform;
6. distribution services will remain available;
7. third-party APIs will remain available;
8. any particular income or financial result will occur.

AI systems can produce unexpected or inaccurate results. You should review generated content before using, licensing, publishing, distributing, or commercially exploiting it.


16. INTELLECTUAL PROPERTY

Lyrixsa AI, its software, website, branding, logos, designs, interfaces, documentation, trademarks, and proprietary technology are owned by or licensed to Geecore Limited and are protected by applicable intellectual-property laws.

Nothing in these Terms transfers ownership of Lyrixsa AI or its underlying technology to you.

Lyrixsa AI and related logos, names, designs, and branding are trademarks or proprietary assets of Geecore Limited or their respective owners.

You may not use our trademarks or branding without prior written permission.


17. LIMITATIONS OF LIABILITY

To the maximum extent permitted by applicable law, Geecore Limited and its officers, directors, employees, contractors, affiliates, licensors, suppliers, and service providers shall not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, including loss of:

- profits;
- revenue;
- business;
- data;
- music;
- content;
- goodwill;
- opportunities;
- royalties;
- or business interruption.

This limitation applies whether the claim arises from contract, negligence, tort, statute, or any other legal theory.

To the extent permitted by law, our total aggregate liability arising from or relating to the Services shall not exceed the amount you paid to Geecore Limited for the applicable Services during the twelve months preceding the event giving rise to the claim.

Some jurisdictions do not permit certain limitations of liability, so some limitations may not apply to you.


18. INDEMNIFICATION

You agree to defend, indemnify, and hold harmless Geecore Limited and its officers, directors, employees, contractors, affiliates, licensors, and service providers from claims, liabilities, damages, losses, costs, and expenses arising from:

1. your use of Lyrixsa AI;
2. your User Content;
3. your violation of these Terms;
4. your violation of applicable law;
5. your infringement of another person's intellectual-property or other rights;
6. your unauthorized use or distribution of music or other content.


19. REVISIONS AND ERRATA

The materials appearing on the Lyrixsa AI website or within the Services may contain technical, typographical, or other errors.

Geecore Limited does not warrant that all information is accurate, complete, or current.

We may modify the Services, features, documentation, pricing, or other materials at any time.

We are not obligated to update previously published information unless required by applicable law.


20. LINKS

Lyrixsa AI may contain links to third-party websites and services.

Geecore Limited has not reviewed all third-party websites and is not responsible for their content, availability, security, privacy practices, or terms.

The inclusion of a link does not constitute an endorsement.

Your use of third-party websites is at your own risk.


21. MODIFICATIONS TO THESE TERMS OF SERVICE

Geecore Limited may revise these Terms from time to time.

When we make material changes, we may update the “Last Updated” date and, where appropriate, provide additional notice.

Your continued use of Lyrixsa AI after revised Terms become effective constitutes acceptance of the revised Terms.


22. TERMINATION

We may suspend or terminate your access to Lyrixsa AI if:

- you violate these Terms;
- you engage in fraudulent activity;
- you abuse the Services;
- you attempt to circumvent technical restrictions;
- you infringe intellectual-property rights;
- your use creates a security or legal risk;
- or we are required to do so by law.

You may stop using Lyrixsa AI at any time.

Upon termination, provisions that by their nature should survive termination will remain in effect, including provisions relating to intellectual property, disclaimers, limitations of liability, indemnification, and dispute resolution.


23. GOVERNING LAW

These Terms shall be governed by and construed in accordance with the laws applicable to Geecore Limited's applicable jurisdiction, without regard to conflict-of-law principles, except where applicable law requires otherwise.

Any dispute shall be resolved in the courts having appropriate jurisdiction, subject to any mandatory rights you may have under applicable law.


24. SOCIAL NETWORKS AND THIRD-PARTY SERVICES

Where you connect Lyrixsa AI to a social network, music platform, distribution service, storage provider, or other third-party service, you authorize the applicable service to provide information and access according to the permissions you approve.

Your relationship with that third-party service remains subject to its own terms and privacy policy.

You may revoke third-party access where the applicable service provides a mechanism for doing so.


25. PRIVACY POLICY

Your privacy is important to us.

Our Privacy Policy explains how Geecore Limited collects, uses, stores, protects, and processes personal information in connection with Lyrixsa AI.

By using Lyrixsa AI, you acknowledge that your information may be processed as described in the applicable Privacy Policy.

We aim to:

- collect information for legitimate and stated purposes;
- collect only information reasonably necessary for those purposes;
- retain information only as reasonably necessary;
- protect information using reasonable security measures;
- provide transparency regarding our data practices;
- and comply with applicable privacy laws.

Where Lyrixsa AI integrates with third-party AI or API services, those services may have their own data-processing terms and policies.


26. CONTACT

For Lyrixsa AI customer support:

Lyrixsa AI Support
https://app.lyrixsa.com/support
support@lyrixsa.com

For general administrative matters relating to Geecore Limited:

Geecore Limited
https://geecorelimited.com/
admin@geecorelimited.com


COPYRIGHT NOTICE

© 2026 Geecore Limited. All Rights Reserved.

Lyrixsa AI is a product of Geecore Limited.

All rights reserved except where expressly stated otherwise.`;

const PRIVACY_TEXT = `PRIVACY POLICY

Last Updated: September 5, 2026

Lyrixsa AI is a product of Geecore Limited (“Geecore,” “we,” “us,” or “our”).

This Privacy Policy explains how we collect, use, process, store, protect, and disclose information when you access or use Lyrixsa AI, including our website, application, software, tools, features, and related services (collectively, the “Services”).

By using Lyrixsa AI, you acknowledge that your information may be collected and processed as described in this Privacy Policy.


ADDITIONAL PRIVACY PRACTICES

A. THIRD-PARTY ACCOUNT ACCESS AND AUTHENTICATION

Lyrixsa AI may allow you to create or access an account using supported authentication methods or to connect certain third-party accounts. If you choose to connect a third-party account, we may receive information that the third party makes available according to the permissions you authorize.

The information received may include account identifiers, name, email address, profile information, authentication information, or other information necessary to provide the connected feature.

You can generally disconnect a connected service through the available Lyrixsa AI controls or through the third-party service. Disconnecting a service may prevent certain features from continuing to function.

B. SOCIAL NETWORKS, MUSIC SERVICES AND EXTERNAL PLATFORMS

Lyrixsa AI may provide integrations or links involving social networks, music streaming services, music distribution platforms, publishing services, or other external platforms.

When you choose to use an integration, information may be transmitted to or received from the applicable third party as necessary to perform the requested function.

For example, a distribution request may require us or our distribution partners to provide information such as artist details, release metadata, audio, artwork, licensing information, or other materials needed to submit a release.

Third-party platforms operate independently and may retain or process information under their own terms and privacy policies. Removing information from Lyrixsa AI does not automatically remove information that has already been submitted to or published by an external platform.

C. PAYMENTS AND ONLINE PURCHASES

Lyrixsa AI may sell digital products, subscriptions, credits, licenses, or related services online. Personal and transaction information needed to complete a purchase may be processed by the applicable payment processor.

Depending on the payment method, information may include payment status, transaction identifiers, purchase details, billing country, and limited payment-related information. The payment processor may separately process payment information under its own privacy policy and security practices.

D. BACKUPS, STORAGE AND DATA RECOVERY

Lyrixsa AI may use cloud hosting, storage, backup, and infrastructure providers to store account information, projects, generated music, uploaded content, metadata, and other information required to operate the Services.

Information may therefore exist in active systems, backups, logs, or disaster-recovery systems for a limited period. Deleting information from an active system does not necessarily mean that every backup copy is immediately destroyed.

Backups are generally allowed to expire or be overwritten according to the applicable backup lifecycle, subject to legal, security, and operational requirements.

E. CONTACT FORMS, SUPPORT AND COMMUNICATION TOOLS

When you contact Lyrixsa AI through customer support, contact forms, email, chat, feedback tools, or similar communication channels, we may collect the information you provide and technical information associated with the communication.

This may include your name, email address, account information, purchase information, messages, attachments, screenshots, and information needed to investigate your request.

We use this information to respond to you, provide support, investigate technical problems, process eligible billing or refund requests, prevent abuse, and improve our Services.

F. ANALYTICS, COOKIES AND PERFORMANCE MEASUREMENT

Lyrixsa AI may use analytics, cookies, pixels, local storage, session technologies, or similar technologies to understand how visitors and users interact with our website and Services.

These technologies may help us measure traffic, feature usage, conversion activity, performance, errors, security events, and general service usage.

Where advertising, remarketing, or conversion-measurement technologies are used, they may process information such as cookies, device identifiers, IP address, browser information, or usage data according to the applicable provider's policies and applicable law.

You may be able to control certain cookies and similar technologies through your browser or device settings.

G. MARKETING AND COMMERCIAL COMMUNICATIONS

Where permitted by law, Lyrixsa AI may use contact information to communicate with users about Lyrixsa AI, Geecore Limited products and services, software launches, offers, related tools, educational opportunities, partner promotions, affiliate offers, and other relevant commercial opportunities.

Marketing communications may be sent in the name of Goodluck Efe, Founder of Lyrixsa AI, representing Lyrixsa AI and/or Geecore Limited. This does not change Geecore Limited's status as the company operating Lyrixsa AI.

You may unsubscribe from promotional communications at any time using the unsubscribe mechanism provided in the message. We may retain limited suppression or opt-out information when reasonably necessary to ensure that we honor your marketing preference.

Transactional, account, security, legal, billing, and other essential service communications may continue after you opt out of marketing.

H. THIRD-PARTY SERVICE PROVIDERS

We may use service providers to perform functions on our behalf, including hosting, storage, analytics, authentication, payments, communications, customer support, security, artificial-intelligence processing, music distribution, and other operational services.

These providers may have access to information only as reasonably necessary to perform their services, subject to applicable contractual, legal, and privacy requirements.

The particular providers we use may change over time as the Services evolve.

I. DATA MINIMIZATION AND RETENTION

We seek to collect and retain information that is reasonably necessary for legitimate purposes connected with the Services.

Retention periods may differ depending on the category of information. Account information and user content may be retained while an account is active. Transaction, licensing, publishing, security, and other records may be retained longer where reasonably necessary for legal, accounting, fraud-prevention, dispute-resolution, contractual, or other legitimate purposes.

When information is no longer reasonably required, we may delete or anonymize it, subject to applicable requirements and our Data Retention and Data Deletion provisions.

J. NO SALE OF PERSONAL INFORMATION

Lyrixsa AI does not sell personal information simply because you use the Services.

We may nevertheless disclose or transfer information to service providers, business partners, payment processors, distribution partners, connected third-party platforms, professional advisers, or authorities where reasonably necessary for the purposes described in this Privacy Policy or as permitted or required by law.

Nothing in this section is intended to limit privacy rights that apply to you under the laws of your jurisdiction.


K. COMMERCIAL AFFILIATIONS & AFFILIATE MARKETING

Lyrixsa AI and/or Geecore Limited may participate in affiliate marketing, joint-venture, referral, reseller, and partner programs. These may include JVZoo, JVShare, Launchpad, ClickBank, Amazon Associates, the AliExpress Affiliate Program, and other applicable affiliate or partner programs.

Affiliate and partner links may use cookies, tracking links, referral identifiers, pixels, and similar technologies to identify the source of visits, attribute sales or conversions, prevent fraud or abuse, measure campaign performance, and calculate commissions or other partner payments.

Lyrixsa AI and/or Geecore Limited may receive a commission or other compensation when a user purchases a product or service through a qualifying affiliate, referral, or partner link. Where applicable, such compensation generally does not increase the price paid by the customer.

Personal Data that may be processed in connection with affiliate and partner activities may include:
- Cookies and similar tracking technologies;
- Usage Data;
- IP address;
- browser and device information;
- referring URL;
- affiliate, referral, or campaign identifiers;
- transaction and conversion information; and
- information reasonably necessary for attribution, fraud prevention, reporting, and partner-program administration.

The specific affiliate, referral, or partner programs used by Lyrixsa AI may change over time.

L. ADVERTISING & AUDIENCE MEASUREMENT

Lyrixsa AI and/or Geecore Limited uses online advertising, conversion tracking, audience measurement, remarketing, and related advertising technologies to promote the Services, measure advertising performance, understand how visitors interact with our website, and improve our marketing campaigns.

GOOGLE ADVERTISING

Lyrixsa AI may use Google advertising and measurement technologies, including Google Ads and related Google advertising, remarketing, conversion-tracking, audience-measurement, and campaign technologies.

These technologies may be used to display or measure advertisements across applicable Google advertising channels and properties, measure conversions, understand campaign performance, create or use advertising audiences, and support remarketing activities where permitted.

Personal Data that may be processed may include:
- Cookies and similar technologies;
- IP address;
- browser and device information;
- Usage Data;
- advertising or device identifiers where applicable;
- website interactions;
- conversion and transaction information; and
- information associated with advertising or remarketing audiences.

META / FACEBOOK ADVERTISING

Lyrixsa AI may use Meta advertising technologies, including Facebook and Instagram advertising, Meta Pixel and related conversion and audience-measurement technologies, remarketing, Custom Audiences, and other applicable Meta advertising services.

These technologies may be used to measure advertising conversions, understand campaign performance, create or use advertising audiences, perform remarketing, and improve the effectiveness of Lyrixsa AI advertising campaigns.

Personal Data that may be processed may include:
- Cookies and similar technologies;
- IP address;
- browser and device information;
- Usage Data;
- advertising identifiers where applicable;
- website and advertising interactions;
- conversion information; and
- information associated with advertising or remarketing audiences.

Google and Meta may process information collected through their advertising technologies according to their respective privacy policies, terms, and applicable data-processing practices.

Where required by applicable law, Lyrixsa AI will provide appropriate notices, choices, consent mechanisms, or other controls relating to advertising cookies and similar technologies.

You may also be able to manage certain advertising preferences through your browser, device settings, or controls made available by the applicable advertising provider.



1. INFORMATION WE COLLECT

Depending on how you use Lyrixsa AI, we may collect information including:

- Name and account information;
- Email address;
- Login and authentication information;
- Billing and transaction information;
- Subscription and purchase information;
- Customer support communications;
- Prompts and instructions submitted to the Services;
- Lyrics, text, images, audio, song references, artwork, and other User Content you submit;
- Music projects, generated content, files, metadata, and related account activity;
- Information relating to your use of Lyrixsa AI features;
- Device and browser information;
- IP address and general technical information;
- Log files, diagnostic information, and usage information;
- Information received from third-party services when you choose to connect them to your Lyrixsa AI account.

We may also collect information that you voluntarily provide when contacting us, submitting feedback, requesting support, participating in promotions, or otherwise communicating with us.


2. INFORMATION YOU PROVIDE

You may provide information directly to us when you:

- create an account;
- purchase a product or subscription;
- use Lyrixsa AI;
- create music;
- upload or submit User Content;
- connect a third-party account;
- contact customer support;
- submit a refund request;
- participate in surveys, promotions, or other activities;
- or otherwise communicate with us.

You are responsible for ensuring that information you submit to Lyrixsa AI is accurate and that you have the right to provide that information.


3. AI MUSIC, PROMPTS AND USER CONTENT

Lyrixsa AI allows users to submit prompts, lyrics, images, audio, song references, and other materials to generate, analyze, edit, or otherwise process music and related content.

Depending on the feature used, information submitted to the Services may need to be processed by Lyrixsa AI and/or third-party technology providers that help us provide the requested functionality.

We may process User Content to:

- provide requested AI generation and music features;
- analyze or transform content when you request those functions;
- provide editing, music analysis, licensing, publishing, or distribution features;
- maintain and operate your projects;
- provide customer support;
- prevent abuse and misuse;
- maintain security;
- troubleshoot technical problems;
- comply with legal obligations.

You should not submit confidential, sensitive, or personal information to an AI generation or processing feature unless you understand why it is being submitted and that you have the right to do so.


4. HOW WE USE INFORMATION

We may use information we collect to:

1. provide, operate, maintain, and improve Lyrixsa AI;
2. create and manage user accounts;
3. process purchases and transactions;
4. provide requested AI music generation and processing features;
5. store and manage user projects and content;
6. provide music licensing, publishing, distribution, and related functionality;
7. provide customer support;
8. communicate with users about accounts, purchases, services, security, and important updates;
9. detect, prevent, and investigate fraud, abuse, unauthorized access, and violations of our Terms of Service;
10. monitor and improve system performance and reliability;
11. comply with legal and regulatory obligations;
12. enforce our agreements and policies;
13. protect our rights, users, systems, and services;
14. perform analytics and understand how the Services are used;
15. develop and improve the Services, subject to applicable law and the settings or commitments applicable to the relevant data.


5. PAYMENT INFORMATION

Payments may be processed by third-party payment processors.

When you purchase Lyrixsa AI, payment information may be collected and processed by the applicable payment provider.

We may receive information such as:

- transaction amount;
- purchase date;
- payment status;
- product purchased;
- billing country;
- limited payment-related information.

We generally do not need to store your complete payment-card number in our own systems when payment processing is handled by a third-party payment processor.

Your use of third-party payment services may also be subject to the provider's own privacy policy and terms.


6. THIRD-PARTY SERVICES AND INTEGRATIONS

Lyrixsa AI may integrate with third-party services, including:

- artificial-intelligence providers;
- cloud hosting and storage providers;
- payment processors;
- music distribution services;
- streaming and music platforms;
- social networks;
- analytics providers;
- authentication providers;
- email and communication providers;
- security and fraud-prevention providers.

If you choose to connect a third-party service to Lyrixsa AI, information may be exchanged with that service as necessary to provide the functionality you requested.

Your use of the third-party service is also governed by that provider's own terms and privacy policy.

Third-party services may process information according to their own policies.


7. SOCIAL NETWORKS AND MUSIC PLATFORMS

Where you connect Lyrixsa AI to a social network, music platform, distribution service, or other external service, we may receive or access information according to the permissions you authorize.

For example, if you authorize a third-party account to connect with Lyrixsa AI, the information made available through that integration may be processed to provide the requested feature.

You may revoke access through the applicable third-party service or through the controls provided by Lyrixsa AI where available.

Revoking access may prevent certain connected features from functioning.


8. LOGS, DEVICE AND TECHNICAL INFORMATION

When you use Lyrixsa AI, we may automatically collect technical information such as:

- IP address;
- browser type;
- operating system;
- device type;
- referring pages;
- pages or features accessed;
- dates and times of access;
- session information;
- error reports;
- performance information;
- general usage information.

We use this information to operate, secure, troubleshoot, analyze, and improve the Services.


9. COOKIES AND SIMILAR TECHNOLOGIES

Lyrixsa AI may use cookies, local storage, pixels, session technologies, and similar technologies.

These technologies may be used to:

- keep you signed in;
- remember preferences;
- maintain security;
- understand website usage;
- measure performance;
- improve user experience;
- support analytics and marketing activities where permitted.

You may be able to control cookies through your browser settings. Disabling certain cookies may cause some features of the Services to function incorrectly.


10. ANALYTICS

We may use analytics and measurement technologies to understand how users interact with Lyrixsa AI.

Analytics information may help us understand:

- which features are used;
- how users navigate the Services;
- technical errors;
- performance;
- conversion and usage patterns;
- and areas where the Services can be improved.

Where applicable, analytics providers may process information according to their own privacy policies.


11. COMMUNICATIONS

We may use your email address to send:

- account-related messages;
- purchase confirmations;
- service notifications;
- security notifications;
- customer support responses;
- important changes to the Services or policies;
- and, where permitted, promotional or marketing communications.

You may unsubscribe from promotional communications by using the unsubscribe mechanism included in those communications.

Transactional and essential service communications may continue even if you opt out of marketing communications.


12. CUSTOMER SUPPORT

If you contact us through:

https://app.lyrixsa.com/support

or

support@lyrixsa.com

we may collect and retain information you provide to help us respond to your request.

Support records may include:

- your name;
- email address;
- account information;
- purchase information;
- messages;
- screenshots or files you voluntarily provide;
- technical information needed to investigate the issue.

We use support information to respond to requests, troubleshoot problems, process refunds where applicable, and improve our Services.


13. HOW WE SHARE INFORMATION

We do not sell your personal information simply because you use Lyrixsa AI.

We may share information where reasonably necessary to operate the Services, including with:

- service providers and contractors;
- AI and technology providers;
- cloud hosting and storage providers;
- payment processors;
- analytics providers;
- email and communication providers;
- security and fraud-prevention providers;
- music distribution and publishing partners when you request those services;
- third-party platforms you choose to connect;
- professional advisers;
- legal authorities where required by law.

Service providers may process information on our behalf and according to applicable agreements and instructions.


14. LEGAL REQUIREMENTS

We may disclose information where we reasonably believe disclosure is necessary to:

- comply with applicable law;
- respond to lawful requests from government authorities;
- enforce our Terms of Service;
- investigate fraud or security incidents;
- protect the rights, property, or safety of Geecore Limited, Lyrixsa AI, our users, or others;
- or prevent unlawful activity.


15. BUSINESS TRANSFERS

If Geecore Limited is involved in a merger, acquisition, financing, reorganization, sale of assets, bankruptcy, or similar transaction, information associated with the Services may be transferred as part of that transaction, subject to applicable law.


16. DATA RETENTION

We retain information for as long as reasonably necessary to:

- provide the Services;
- maintain accounts and projects;
- complete transactions;
- provide customer support;
- meet legal, accounting, or regulatory requirements;
- resolve disputes;
- enforce agreements;
- prevent fraud and abuse;
- maintain security;
- and fulfill other legitimate business purposes.

Retention periods may vary depending on the type of information and the reason it was collected.

When information is no longer reasonably required, we may delete, anonymize, or otherwise dispose of it, subject to applicable legal and operational requirements.


17. DATA SECURITY

We use reasonable administrative, technical, and organizational safeguards designed to protect information against unauthorized access, disclosure, alteration, misuse, or destruction.

However, no method of transmission, storage, or electronic security can be guaranteed to be completely secure.

Accordingly, we cannot guarantee absolute security of information.


18. INTERNATIONAL DATA PROCESSING

Depending on where you use Lyrixsa AI and where our service providers operate, your information may be processed or stored in countries other than the country in which you live.

Those countries may have different data-protection laws.

By using the Services, you acknowledge that information may be processed internationally, subject to applicable legal requirements.


19. CHILDREN'S PRIVACY

Lyrixsa AI is not intended for children who are below the minimum age required to independently use the Services under applicable law.

We do not knowingly seek to collect personal information from children in violation of applicable law.

If you believe a child has provided personal information to us inappropriately, please contact us at support@lyrixsa.com.


20. YOUR PRIVACY RIGHTS

Depending on where you live, you may have rights concerning your personal information, which may include:

- requesting access to personal information;
- requesting correction of inaccurate information;
- requesting deletion of certain information;
- requesting information about how your data is processed;
- objecting to or restricting certain processing;
- requesting portability of certain information;
- withdrawing consent where processing is based on consent;
- or submitting a complaint to a relevant privacy authority.

These rights vary by jurisdiction and may be subject to legal exceptions.

To make a privacy-related request, contact:

support@lyrixsa.com

or use:

https://app.lyrixsa.com/support

We may need to verify your identity before completing certain requests.


21. ACCOUNT AND DATA DELETION

Where available, you may request deletion of your Lyrixsa AI account and associated personal information by contacting support.

Please note that certain information may need to be retained where required by law, necessary for legitimate business purposes, required to resolve disputes, or necessary to prevent fraud or abuse.

Deleting an account may also result in loss of access to projects, generated content, credits, account history, or other Services associated with that account, subject to applicable policies and law.


22. USER CONTENT AND PRIVACY

You are responsible for determining what information you submit to Lyrixsa AI.

Do not submit personal information about another person unless you have the appropriate authorization or legal basis to do so.

You should also avoid placing passwords, payment-card information, government identification numbers, confidential business information, or other highly sensitive information into AI prompts or other fields unless the relevant feature specifically requires it and you understand how the information will be processed.


23. AI SERVICE PROVIDERS

Some Lyrixsa AI features may be powered by third-party artificial-intelligence technologies.

When a feature requires an external AI provider, information necessary to perform the requested operation may be transmitted to that provider.

The provider may process the information according to its applicable terms, privacy policies, and data-processing practices.

The specific providers used by Lyrixsa AI may change as we improve, replace, or expand the Services.


24. AI TRAINING AND SERVICE IMPROVEMENT

We may use certain information, usage data, technical data, feedback, and other permitted information to operate, maintain, secure, analyze, and improve Lyrixsa AI.

Where third-party AI providers are used, their own policies may determine whether submitted content is used for model training or other purposes.

We will not intentionally represent that a third-party provider's data practices are the same as Geecore Limited's practices.

Where applicable, users should review the relevant provider's current privacy and data-use terms for additional information.


25. LINKS TO OTHER WEBSITES

Lyrixsa AI may contain links to third-party websites.

We are not responsible for the privacy practices, security, content, or policies of third-party websites.

We encourage you to review the privacy policies of websites and services you visit through links provided by Lyrixsa AI.


26. CHANGES TO THIS PRIVACY POLICY

We may update this Privacy Policy from time to time.

When we make changes, we may update the “Last Updated” date and, where appropriate, provide additional notice.

Your continued use of Lyrixsa AI after an updated Privacy Policy becomes effective means that you acknowledge the updated policy, subject to applicable law.


27. CONTACT US

For Lyrixsa AI privacy questions, requests, or concerns:

Lyrixsa AI
A product of Geecore Limited

Website:
http://lyrixsa.com/

Customer Support:
https://app.lyrixsa.com/support

Support Email:
support@lyrixsa.com

For general administrative matters relating to Geecore Limited:

Website:
https://geecorelimited.com/

Administrative Email:
admin@geecorelimited.com/


COPYRIGHT NOTICE

© 2026 Geecore Limited. All Rights Reserved.

Lyrixsa AI is a product of Geecore Limited.

All rights reserved except where expressly stated otherwise.`;

const LICENSE_TEXT = `LYRIXSA AI
COMMERCIAL MUSIC LICENSE AGREEMENT

Effective Date: September 4, 2026

Licensor:
Geecore Limited, operating Lyrixsa AI ("Lyrixsa", "we", "us", or "our")

Licensee:
The individual or legal entity that purchases an eligible Lyrixsa AI commercial-use product ("Licensee", "you", or "your").

IMPORTANT
This Commercial Music License Agreement ("License") governs the commercial-use rights granted for eligible music generated through Lyrixsa AI. It should be read together with the Lyrixsa AI Terms of Service and Privacy Policy. If there is a conflict, the applicable Terms of Service and the specific product/offer terms control to the extent stated there.

1. DEFINITIONS

1.1 "Generated Music" means an audio track, song, instrumental, composition, lyrics, stems, or other music output generated through eligible Lyrixsa AI features.

1.2 "Licensed Music" means Generated Music that qualifies for commercial use under the Licensee's purchased Lyrixsa AI plan and that was generated in accordance with the Lyrixsa AI Terms of Service and applicable product rules.

1.3 "Commercial Use" means lawful use of Licensed Music for business, professional, promotional, monetized, or revenue-generating purposes, including use in monetized videos, advertisements, social media content, podcasts, courses, games, websites, client projects, branded content, public performances, and music distribution, subject to this License and applicable third-party platform rules.

1.4 "License Certificate" means any downloadable document issued by Lyrixsa AI identifying an eligible Licensed Music track and providing supporting evidence of the applicable commercial-use license.

2. ELIGIBILITY

2.1 Commercial-use rights are available only for Generated Music that is expressly covered by an eligible Lyrixsa AI commercial-use product, purchase, subscription, or offer.

2.2 The Licensee must have obtained the applicable commercial-use rights before using a track commercially.

2.3 Unless the applicable product terms expressly state otherwise, music generated under free access, non-commercial access, promotional access, or a plan that does not include commercial rights is not covered by this License.

2.4 Commercial rights do not automatically apply to third-party music, samples, recordings, lyrics, images, voices, names, likenesses, or other material supplied by the Licensee.

3. LICENSE GRANT

3.1 Subject to this License, Lyrixsa grants the Licensee a non-exclusive, worldwide, royalty-free commercial license to use eligible Licensed Music for lawful commercial and non-commercial purposes.

3.2 For an eligible track, the Licensee may:

- Use the track in monetized YouTube, TikTok, Instagram, Facebook, and other content, subject to the rules of those platforms.
- Use the track in advertisements, marketing campaigns, promotional materials, courses, podcasts, websites, presentations, games, films, videos, and other creative projects.
- Synchronize the track with visual or other media.
- Reproduce, edit, trim, arrange, mix, remix, adapt, or otherwise modify the track for legitimate creative purposes.
- Publicly perform, stream, broadcast, or otherwise communicate the track to the public.
- Distribute an eligible track through music distribution services and digital streaming platforms where the distributor and destination platform accept the content.
- Sell or license a permitted recording or larger creative work containing the Licensed Music, provided the transaction does not falsely represent Lyrixsa AI's underlying technology, platform, or trademarks as being transferred.
- Use the track for client work and commercial projects performed for customers or organizations.

3.3 The License is intended to permit commercial exploitation of eligible Licensed Music without an additional royalty payable to Lyrixsa solely because the music is commercially used, subject to any separate fees expressly stated for Lyrixsa services, publishing, distribution, or other products.

4. DURATION AND SURVIVAL

4.1 For eligible music covered by a perpetual commercial-use purchase, the commercial-use rights granted under this License continue for that eligible music after cancellation, expiration, or termination of the user's account, except where the License is validly revoked because of a material breach or where applicable law requires otherwise.

4.2 If a particular Lyrixsa AI offer specifies a different license duration or condition, that offer's terms control for the affected music.

4.3 The License does not grant continued access to the Lyrixsa AI software, account, models, dashboard, credits, or other platform services after a subscription or purchase ends.

5. OWNERSHIP AND COPYRIGHT

5.1 Lyrixsa retains all rights in and to the Lyrixsa AI platform, software, models, technology, trademarks, branding, documentation, and service infrastructure.

5.2 This License grants commercial-use rights in eligible Licensed Music. It does not transfer ownership of the Lyrixsa AI platform or its underlying technology.

5.3 Copyright protection for AI-generated material can vary by jurisdiction and may depend on the degree of human authorship and other legally relevant facts. Nothing in this License guarantees that a particular Generated Music track qualifies for copyright protection in every jurisdiction.

5.4 Where the Licensee contributes original lyrics, recordings, compositions, performances, edits, arrangements, or other human-authored material, the Licensee remains responsible for determining and protecting any rights that may arise from those contributions.

6. USER INPUTS AND THIRD-PARTY MATERIAL

6.1 The Licensee is solely responsible for having the necessary rights, permissions, and consents for material submitted to Lyrixsa AI.

6.2 The License does not give the Licensee permission to use another person's copyrighted recording, composition, lyrics, trademark, voice, name, likeness, or other protected material.

6.3 If the Licensee uses a supported song reference, Spotify link, Apple Music link, upload, sample, or other third-party reference as an input, the Licensee is responsible for complying with all applicable rights and platform rules relating to that input.

6.4 A Lyrixsa-generated output is not a license to reproduce or distribute the original third-party reference material.

7. AI-GENERATED MUSIC AND SIMILARITY

7.1 AI systems can produce outputs that may be similar in style, structure, sound, or characteristics to existing music.

7.2 Lyrixsa does not guarantee that every Generated Music track is unique in every musical characteristic or that no third party will independently create a similar work.

7.3 The Licensee must not deliberately use Licensed Music to impersonate a real artist, falsely imply endorsement by a real artist, or violate a person's publicity, privacy, trademark, or other rights.

8. MUSIC DISTRIBUTION

8.1 Eligible Licensed Music may be submitted to music distributors and digital music platforms, subject to their current rules, policies, technical requirements, and acceptance procedures.

8.2 Lyrixsa does not guarantee that Spotify, Apple Music, YouTube Music, Amazon Music, Tidal, Deezer, TikTok, Instagram/Facebook, Boomplay, Tencent/QQ Music, any distributor, performing rights organization, collecting society, or other third party will accept, retain, monetize, or promote a particular track.

8.3 The Licensee is responsible for providing accurate metadata and complying with the rules of the distributor and destination platform.

8.4 Additional distribution, publishing, collection, or administration services may be subject to separate terms and fees.

9. ROYALTIES AND MONETIZATION

9.1 Subject to the applicable distributor, platform, publishing, collection, and service terms, the Licensee may monetize eligible Licensed Music and retain royalties or other revenue payable to the Licensee.

9.2 Lyrixsa does not guarantee any minimum amount of streams, sales, royalties, advertising revenue, or other income.

9.3 Third-party platforms may deduct their own fees, commissions, taxes, or other amounts before payment.

10. CONTENT ID, RIGHTS MANAGEMENT, AND FINGERPRINTING

10.1 Commercial use under this License does not by itself guarantee that a third-party content-identification or rights-management service will accept a track.

10.2 The Licensee must comply with the rules of the selected distributor and rights-management service before submitting Licensed Music to Content ID, Rights Manager, fingerprinting databases, or similar systems.

10.3 The Licensee must not use any rights-management system to make a false claim of exclusive ownership over material when the Licensee does not have the necessary rights to make that claim.

11. RESTRICTIONS

The Licensee may not:

- Use Licensed Music for unlawful purposes.
- Use third-party material without the necessary rights or permissions.
- Falsely claim that a real artist performed, endorsed, or authorized the music.
- Use the Lyrixsa AI name, trademarks, software, or branding as though they were transferred to the Licensee.
- Resell or sublicense access to the Lyrixsa AI platform itself.
- Use Licensed Music to train, develop, or improve a competing generative AI music model where prohibited by applicable law or platform terms.
- Circumvent technical, account, credit, security, or licensing restrictions.
- Use Licensed Music in a manner that violates applicable law or third-party platform policies.

12. LICENSE CERTIFICATE

12.1 Where Lyrixsa AI provides a commercial license certificate for an eligible track, the certificate is supporting documentation of the commercial-use rights associated with that track.

12.2 A certificate may identify information such as the Licensee, account, track title, track ID, license ID, issue date, and applicable plan or purchase.

12.3 A certificate does not create rights broader than those granted by this License or the applicable Lyrixsa AI product terms.

12.4 The Licensee should retain the certificate and related purchase/account records for their records.

13. NO WARRANTY OF PLATFORM ACCEPTANCE

13.1 This License does not guarantee that any distributor, streaming service, social network, marketplace, advertising platform, rights society, collecting society, or other third party will accept a Licensed Music track.

13.2 Third-party platforms may establish their own requirements concerning AI-generated music, copyright, metadata, Content ID, monetization, disclosure, or distribution.

14. LIMITATION OF LIABILITY

To the maximum extent permitted by applicable law, Lyrixsa and Geecore Limited will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for loss of profits, revenue, streams, sales, data, goodwill, or business opportunities arising from use of Licensed Music or from actions taken by third-party platforms, distributors, rights organizations, or other services.

15. LICENSE REVOCATION

15.1 Lyrixsa may suspend or revoke commercial-use rights for an affected track if the Licensee materially breaches this License, the Terms of Service, or applicable law.

15.2 Revocation does not affect rights that cannot lawfully be revoked.

15.3 Where a license is revoked, the Licensee must stop new commercial exploitation of the affected track and take reasonable steps to comply with the revocation notice, subject to applicable law.

16. RELATIONSHIP TO TERMS OF SERVICE

This License forms part of the Lyrixsa AI legal framework and should be read together with the Lyrixsa AI Terms of Service and Privacy Policy.

17. CHANGES

Lyrixsa may update this License from time to time. The version applicable to a particular Licensed Music track will be determined in accordance with the applicable Terms of Service and product terms.

18. CONTACT

Lyrixsa AI
A product of Geecore Limited

Website:
http://lyrixsa.com/

Support:
https://app.lyrixsa.com/support

Email:
support@lyrixsa.com

© 2026 Geecore Limited. All Rights Reserved.

LEGAL NOTICE
This document is a commercial licensing template for Lyrixsa AI and is not legal advice. Before publishing or relying on this License as a binding legal document, Geecore Limited should have qualified legal counsel review it for the jurisdictions in which Lyrixsa AI operates and for the exact rights provided by each underlying AI/music provider.`;

const LEGAL_SOURCES: Record<LegalDocumentId, LegalSource> = {
  terms: {
    id: 'terms',
    title: 'Terms of Service',
    summary: 'The terms that govern access to and use of Lyrixsa AI.',
    version: '1.0',
    text: TERMS_TEXT,
  },
  privacy: {
    id: 'privacy',
    title: 'Privacy Policy',
    summary: 'How Lyrixsa AI collects, uses, stores, protects and discloses information.',
    version: '1.0',
    text: PRIVACY_TEXT,
  },
  'license-agreement': {
    id: 'license-agreement',
    title: 'License Agreement',
    summary: 'Commercial-use rights for eligible music generated through Lyrixsa AI.',
    version: '1.0',
    text: LICENSE_TEXT,
  },
};

function legalDate(text: string): string {
  const dated = text.match(/(?:Last Updated|Effective Date):\s*([A-Za-z]+) (\d{1,2}), (\d{4})/);
  if (!dated) return '2026-09-05';
  const [, monthName, day, year] = dated;
  const month = new Date(`${monthName} 1, ${year}`).getMonth() + 1;
  return `${year}-${String(month).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`;
}

function headingFor(line: string, id: LegalDocumentId): string | null {
  if (/^\d+\. [A-Z0-9]/.test(line)) return line;
  if (id === 'privacy' && /^[A-Z]\. [A-Z0-9]/.test(line)) return line;
  if (/^(ADDITIONAL PRIVACY PRACTICES|GOOGLE ADVERTISING|META \/ FACEBOOK ADVERTISING|COPYRIGHT NOTICE|IMPORTANT|LEGAL NOTICE)$/.test(line)) {
    return line;
  }
  return null;
}

function pushParagraph(paragraphs: string[], lines: string[]): void {
  if (lines.length === 0) return;
  paragraphs.push(lines.join(' '));
  lines.length = 0;
}

function documentFromSource(source: LegalSource): LegalDocument {
  const lines = source.text.replace(/\r/g, '').split('\n').map((line) => line.trim());
  const dateIndex = lines.findIndex((line) => /^(Last Updated|Effective Date):/.test(line));
  const body = lines.slice(dateIndex >= 0 ? dateIndex + 1 : 0);
  const clauses: { title: string; paragraphs: string[] }[] = [];
  let current = { title: 'Introduction', paragraphs: [] as string[] };
  const paragraphLines: string[] = [];

  for (const line of body) {
    if (!line) {
      pushParagraph(current.paragraphs, paragraphLines);
      continue;
    }

    const heading = headingFor(line, source.id);
    if (heading) {
      pushParagraph(current.paragraphs, paragraphLines);
      if (current.paragraphs.length > 0 || current.title !== 'Introduction') clauses.push(current);
      current = { title: heading, paragraphs: [] };
      continue;
    }

    if (/^[-•]/.test(line) || /^\d+\. [a-z]/.test(line)) {
      pushParagraph(current.paragraphs, paragraphLines);
      current.paragraphs.push(line);
      continue;
    }

    paragraphLines.push(line);
  }

  pushParagraph(current.paragraphs, paragraphLines);
  if (current.paragraphs.length > 0 || current.title !== 'Introduction') clauses.push(current);

  return {
    id: source.id,
    title: source.title,
    summary: source.summary,
    updated: legalDate(source.text),
    version: source.version,
    clauses,
  };
}

export const LEGAL_DOCUMENTS: Record<LegalDocumentId, LegalDocument> = {
  terms: documentFromSource(LEGAL_SOURCES.terms),
  privacy: documentFromSource(LEGAL_SOURCES.privacy),
  'license-agreement': documentFromSource(LEGAL_SOURCES['license-agreement']),
};

export const LEGAL_DOCUMENT_ORDER: LegalDocumentId[] = ['terms', 'privacy', 'license-agreement'];

/** The two documents signup requires acceptance of. Their versions go in the consent record. */
export const REQUIRED_CONSENT_DOCUMENTS: LegalDocumentId[] = ['terms', 'privacy'];
